/**
 * Combines finalized transcript text with optional interim chunks into display lines
 */
export type TranscriptDisplayChunk = { speaker: string; text: string };

/** Chunk contract the line builders consume. Both sources satisfy it: legacy
 * live chunks (`LiveTranscriptChunk`) and projected utterances; `isFinal`
 * marks committed-but-unflushed text that renders at normal color. */
export type TranscriptInterimChunk = TranscriptDisplayChunk & {
  isFinal?: true;
};

export type LineSegment = { text: string; isFinal: boolean };

/** Runs are kept in arrival order, which is what keeps the dimmed display and the
 * copied string from disagreeing on word order. */
export type TranscriptDisplayLine = {
  speaker: string | null;
  segments: LineSegment[];
};

const SPEAKER_TAG_REGEX = /^\[([^\]]+)\]:\s*/;

/** Single source of truth for merging the committed transcript with live interim
 * chunks; the string projection and the renderer both derive from it. */
export function buildTranscriptDisplayLines(
  transcriptValue: string | null,
  interimChunks: readonly TranscriptInterimChunk[] = []
): TranscriptDisplayLine[] {
  const lines: TranscriptDisplayLine[] = transcriptValue
    ? transcriptValue
        .split('\n')
        .filter((line) => line.trim())
        .map(seedLineFromCommitted)
    : [];

  for (const chunk of interimChunks) {
    const lastLine = lines[lines.length - 1];
    const segment: LineSegment = { text: chunk.text, isFinal: !!chunk.isFinal };
    if (lastLine && lastLine.speaker === chunk.speaker) {
      lastLine.segments.push(segment);
    } else {
      lines.push({ speaker: chunk.speaker, segments: [segment] });
    }
  }

  return lines;
}

function seedLineFromCommitted(line: string): TranscriptDisplayLine {
  const tagMatch = line.match(SPEAKER_TAG_REGEX);
  if (!tagMatch) {
    return { speaker: null, segments: [{ text: line, isFinal: true }] };
  }
  return {
    speaker: tagMatch[1],
    segments: [{ text: line.slice(tagMatch[0].length), isFinal: true }],
  };
}

/** Shared by the string projection and in-transcript search so both match the
 * exact text the renderer shows. */
export function joinSegmentText(segments: readonly LineSegment[]): string {
  return segments.map((segment) => segment.text).join(' ');
}

export function getSpeakerDisplay(
  lines: readonly { speaker: string | null; isOthers: boolean }[],
  index: number
): { isSpeakerChange: boolean; showSpeaker: boolean } {
  const line = lines[index];
  const previous = lines[index - 1];
  const isSpeakerChange =
    index > 0 &&
    (previous.isOthers !== line.isOthers || previous.speaker !== line.speaker);
  const showSpeaker = !!line.speaker && (index === 0 || isSpeakerChange);
  return { isSpeakerChange, showSpeaker };
}

/** String projection for the copy, word-count, and cmdk callers. Committed
 * lines round-trip through the display model, so whitespace after a speaker
 * tag normalizes to a single space — the projection matches what the renderer
 * shows, not the stored bytes. Every caller tolerates this: copy, search,
 * word count, and the length telemetry are all whitespace-insensitive. */
export function buildTranscriptLines(
  transcriptValue: string | null,
  interimChunks: readonly TranscriptInterimChunk[] = []
): string[] {
  return (
    buildTranscriptDisplayLines(transcriptValue, interimChunks)
      // Matches the renderer, which hides empty paragraph-break headers
      .filter((line) => joinSegmentText(line.segments).trim())
      .map((line) => {
        const prefix = line.speaker ? `[${line.speaker}]: ` : '';
        return prefix + joinSegmentText(line.segments);
      })
  );
}

export function splitTranscriptSegments<
  T extends { speaker: string; text: string },
>(segments: readonly T[]): T[] {
  const result: T[] = [];
  for (const segment of segments) {
    const trimmed = segment.text.trim();
    if (!trimmed) continue;

    const sentences: string[] = [];
    const re = /[.!?]\s+/g;
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(trimmed)) !== null) {
      sentences.push(trimmed.slice(last, match.index + 1));
      last = match.index + match[0].length;
    }
    if (last < trimmed.length) sentences.push(trimmed.slice(last));

    for (const sentence of sentences) {
      if (sentence.trim()) result.push({ ...segment, text: sentence });
    }
  }
  return result.length > 0 ? result : (segments as T[]);
}

export function buildRenamedSpeakerSegments(
  transcriptValue: string,
  currentLabel: string,
  newLabel: string
): TranscriptDisplayChunk[] | null {
  const segments: TranscriptDisplayChunk[] = [];
  let previousSpeaker: string | null = null;
  for (const line of buildTranscriptDisplayLines(transcriptValue)) {
    const speaker: string | null = line.speaker ?? previousSpeaker;
    if (speaker === null) return null;
    previousSpeaker = speaker;
    segments.push({
      speaker: speaker === currentLabel ? newLabel : speaker,
      text: joinSegmentText(line.segments),
    });
  }
  return segments;
}

/**
 * Formats a final transcript delta for appending to transcript text.
 * Handles speaker change detection, proper spacing, and paragraph breaks.
 *
 * This logic is duplicated in the backend to enable optimistic UI — the
 * client formats and appends deltas locally instead of waiting for the full
 * transcript from the backend.
 *
 * Mirrored in the backend as `format_transcript_delta` in
 * `backend/lb/features/meetings/utils/transcript_formatting.py`.
 * Changes here must be replicated there and vice-versa.
 */
export const PARAGRAPH_BREAK_THRESHOLD = 500;

export function formatTranscriptDelta(
  currentTranscript: string,
  currentSpeaker: string | undefined,
  delta: { speaker: string; text: string }
): string {
  if (delta.speaker !== currentSpeaker) {
    const prefix = currentTranscript
      ? `\n[${delta.speaker}]: `
      : `[${delta.speaker}]: `;
    return prefix + delta.text;
  }

  const needsSpace =
    currentTranscript &&
    !currentTranscript.endsWith(' ') &&
    !currentTranscript.endsWith('\n') &&
    !delta.text.startsWith(' ') &&
    !delta.text.startsWith('\n');
  let result = needsSpace ? ' ' + delta.text : delta.text;

  // Paragraph break: same speaker, >=threshold chars since last line break, delta ends with '.'
  const lastNewline = currentTranscript.lastIndexOf('\n');
  const charsSinceBreak =
    lastNewline !== -1
      ? currentTranscript.length - lastNewline - 1
      : currentTranscript.length;
  if (
    charsSinceBreak + result.length >= PARAGRAPH_BREAK_THRESHOLD &&
    delta.text.trimEnd().endsWith('.')
  ) {
    result += `\n[${delta.speaker}]: `;
  }

  return result;
}
